import { Users, Target, Film, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export type TrafficLightStatus = 'green' | 'yellow' | 'red';

interface TrafficLightCardProps {
  title: string;
  status: TrafficLightStatus;
  summary: string;
  onClick?: () => void;
  className?: string;
  compact?: boolean;
}

export const TrafficLightCard = ({
  title,
  status,
  summary,
  onClick,
  className,
}: TrafficLightCardProps) => {
  
  const getStatusConfig = (status: TrafficLightStatus) => {
    switch (status) {
      case 'green':
        return {
          label: "High", // Or 'Ready' depending on context, keeping generic for now or mapping below
          badgeBg: "bg-green-500/10",
          badgeBorder: "border-green-500/20",
          badgeText: "text-green-700 dark:text-green-400",
          dotBg: "bg-green-500",
          dotShadow: "shadow-[0_0_8px_rgba(34,197,94,0.6)]"
        };
      case 'yellow':
        return {
          label: "Medium",
          badgeBg: "bg-yellow-500/10",
          badgeBorder: "border-yellow-500/20",
          badgeText: "text-yellow-700 dark:text-yellow-400",
          dotBg: "bg-yellow-500",
          dotShadow: "shadow-[0_0_8px_rgba(234,179,8,0.6)]"
        };
      case 'red':
        return {
          label: "Low",
          badgeBg: "bg-red-500/10",
          badgeBorder: "border-red-500/20",
          badgeText: "text-red-700 dark:text-red-400",
          dotBg: "bg-red-500",
          dotShadow: "shadow-[0_0_8px_rgba(239,68,68,0.6)]"
        };
      default:
        return {
          label: "Unknown",
          badgeBg: "bg-slate-500/10",
          badgeBorder: "border-slate-500/20",
          badgeText: "text-slate-700 dark:text-slate-400",
          dotBg: "bg-slate-500",
          dotShadow: "shadow-none"
        };
    }
  };

  const getIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("reach")) return Users;
    if (lowerTitle.includes("brand")) return Target;
    if (lowerTitle.includes("production")) return Film;
    return HelpCircle;
  };

  const getStatusLabel = (title: string, status: TrafficLightStatus) => {
    // Custom labels based on title to match HTML example
    if (title.toLowerCase().includes("production")) {
        if (status === 'green') return "Ready";
        if (status === 'yellow') return "Check";
        if (status === 'red') return "Not Ready";
    }
    // Default mapping
    if (status === 'green') return "High";
    if (status === 'yellow') return "Medium";
    if (status === 'red') return "Low";
    return "Unknown";
  }

  const config = getStatusConfig(status);
  const Icon = getIcon(title);
  const statusLabel = getStatusLabel(title, status);

  return (
    <button
      onClick={onClick}
      className={cn(
        "group relative p-5 bg-card border border-border rounded-2xl hover:border-primary/50 hover:shadow-lg transition-all text-left flex flex-col gap-3 overflow-hidden h-full",
        className
      )}
    >
      <div className="flex justify-between items-start z-10 w-full">
        <div className="p-2 rounded-lg bg-background border border-border text-foreground shadow-sm">
          <Icon size={18} strokeWidth={1.5} />
        </div>
        <div className={cn("flex items-center gap-1.5 px-2 py-0.5 rounded-full border", config.badgeBg, config.badgeBorder)}>
          <div className={cn("w-1.5 h-1.5 rounded-full", config.dotBg, config.dotShadow)}></div>
          <span className={cn("text-[10px] font-bold uppercase tracking-wide", config.badgeText)}>
            {statusLabel}
          </span>
        </div>
      </div>
      <div className="z-10 mt-1">
        <h3 className="font-semibold font-cabinet text-foreground text-base">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground mt-1 font-ibm leading-relaxed line-clamp-2">
          {summary}
        </p>
      </div>
    </button>
  );
};